/* eslint-disable react/no-unknown-property */
import { ReactElement, useMemo } from 'react';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { ConfigProvider } from 'antd';
import { OnboardingStateContextProvider } from 'context/onboarding/OnboardingStateContext';
import { SidebarContextProvider } from 'context/SidebarContext';
import { TutorialContextProvider } from 'context/TutorialContext';
import { PostHogProvider } from 'posthog-js/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import ErrorBoundary from '@components/ErrorBoundary';
import configureAmplify from '@lib/amplify.lib';
import { ClarityLifecycle } from '@lib/analytics/ClarityLifecycle';
import { PostHogAnalyticsLifecycle } from '@lib/analytics/PostHogAnalyticsLifecycle';
import { AuthProvider } from '@lib/auth';
import { GoogleMapsApi } from '@lib/locations';
import { posthog } from '@lib/posthog.lib';
import { store } from '@lib/redux.lib';
import { getRenderPage, NextPageWithLayout } from '@lib/utils/pageHelper';
import { getIsPublicApplicationViewFromRouterAsPath } from '@lib/utils/urlHelper';
import antTheme from '@styles/antTheme';
import theme from '@styles/theme';
import { SignupPageProvider } from '@views/auth/signup/SignupPageContext';

import PublicApplicationPage from './apps/[application_id]';
import ProtectRoute from './auth/protect';

import '@styles/global.css';
import '@styles/onboardingStyles.css';
import '@styles/reset.css';
import 'antd/dist/reset.css';

configureAmplify();

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const queryClient = new QueryClient();

const JobsApp = ({
  Component,
  pageProps,
}: AppPropsWithLayout): ReactElement => {
  const router = useRouter();

  const isPublicApplicationView = useMemo(
    () => getIsPublicApplicationViewFromRouterAsPath(router?.asPath),
    [router?.asPath]
  );

  const routeProtected = (() => {
    if (isPublicApplicationView) {
      return !PublicApplicationPage?.isPublic;
    }

    return !Component?.isPublic;
  })();

  const Page = (() => {
    if (isPublicApplicationView) {
      return getRenderPage(PublicApplicationPage, pageProps);
    }

    return getRenderPage(Component, pageProps);
  })();

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <ReduxProvider store={store}>
          <ThemeProvider theme={theme}>
            <OnboardingStateContextProvider>
              <AuthProvider>
                <ErrorBoundary>
                  <ConfigProvider theme={antTheme}>
                    <SignupPageProvider>
                      <PostHogAnalyticsLifecycle />
                      <ClarityLifecycle />
                      <ProtectRoute routeProtected={routeProtected}>
                        <SidebarContextProvider>
                          <TutorialContextProvider>
                            <GoogleMapsApi />
                            <main>{Page}</main>
                          </TutorialContextProvider>
                        </SidebarContextProvider>
                      </ProtectRoute>
                    </SignupPageProvider>
                  </ConfigProvider>
                </ErrorBoundary>
              </AuthProvider>
            </OnboardingStateContextProvider>
          </ThemeProvider>
        </ReduxProvider>
      </QueryClientProvider>
    </PostHogProvider>
  );
};

export default JobsApp;
